import { Injectable, Injector, Type, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

/**
 * A second, independent overlay container (its own <div>, styled with a z-index above
 * the away-lock screen in styles.scss) so dialogs opened from the lock flow — wrong-MPIN
 * error, Forgot MPIN's 2FA/reset dialogs, Logout confirmation — render above the lock
 * screen without touching the app-wide `.cdk-overlay-container`. That container is left
 * completely alone so any popup the user had open before going idle stays open, exactly
 * as they left it, underneath the lock screen until they unlock.
 */
class LockOverlayContainer extends OverlayContainer {
  protected override _createContainer(): void {
    super._createContainer();
    this._containerElement.classList.add('lock-overlay-container');
  }
}

@Injectable({
  providedIn: 'root',
})
export class LockDialogService {
  private _parentInjector = inject(Injector);
  private _document = inject(DOCUMENT);
  private _platform = inject(Platform);
  private _dialog: MatDialog | null = null;

  private get _scopedDialog(): MatDialog {
    if (!this._dialog) {
      const injector = Injector.create({
        parent: this._parentInjector,
        providers: [
          { provide: OverlayContainer, useFactory: () => new LockOverlayContainer(this._document, this._platform) },
          Overlay,
          MatDialog,
        ],
      });
      this._dialog = injector.get(MatDialog);
    }
    return this._dialog;
  }

  open<T, D = any, R = any>(component: Type<T>, config?: MatDialogConfig<D>): MatDialogRef<T, R> {
    return this._scopedDialog.open(component, config);
  }

  /**
   * Closes any dialog currently open in this lock-scoped overlay (e.g. the
   * Logout confirmation) — used when a session-expire error needs to reach
   * the user and can't rely on its own z-index beating this container's.
   */
  closeAll(): void {
    this._dialog?.closeAll();
  }
}
